<template>
  <v-layout row justify-center>
    <v-dialog :value="show" scrollable width="60%" persistent>
      <v-card>
        <v-card-title primary-title>
          <div class="title">{{ $t('rms.media.uploader.dialog.title') }}</div>

          <v-spacer></v-spacer>
          <v-btn
            v-if="!is_uploading"
            small
            dark
            icon
            color="grey"
            @click.native="$emit('close')"
            class="mx-0"
          >
            <v-icon small>$vuetify.icons.rms_close</v-icon>
          </v-btn>
          <ConfirmationDialog
            v-if="is_uploading"
            :desc="'assets_manager.upload.confirmation.description'"
            :action="getDialogAction(null, 'rms_close', 'white')"
            :headline="'assets_manager.upload.confirmation.heading'"
            :button_type="'icon-button'"
          ></ConfirmationDialog>
        </v-card-title>
        <v-card-text>
          <v-container fluid>
            <GDriveSelector v-if="upload_type == 'gdriveUpload'"
              :film_id="model.id"
              @complete="queueCompelete"
             />
            <vue-dropzone
              v-else
              ref="myVueDropzone"
              id="dropzone"
              :options="dropzoneOptions"
              :useCustomSlot="true"
              @vdropzone-success="successUpload"
              @vdropzone-queue-complete="queueCompelete"
              @vdropzone-files-added="handleFilesAdded"
              @vdropzone-error="reportError"
              @vdropzone-sending="appendData"
            >
              <div class="dropzone-custom-content">
                <v-icon color="blue-grey lighten-4" style="font-size: 70px"
                  >mdi-cloud-upload</v-icon
                >
                <h2 color="info" class="dropzone-custom-title">
                  {{ $t('rms.drag_and_drop.message') }}
                </h2>
                <div class="subtitle">
                  {{ $t('rms.drag_and_drop.subtitle') }}
                </div>
              </div>
            </vue-dropzone>
            <div v-if="dropzone_custom_error" class="mt-2">
              <v-alert :value="true" type="error">
                {{ dropzone_custom_error }}
              </v-alert>
            </div>
            <div v-if="_isImgUploading()" class="mt-4 secondary--text font-weight-bold">
                <ul>
                  <li>{{ $t('upload.img.inst_1') }}</li>
                  <li>{{ $t('upload.img.inst_2') }}</li>
                </ul>
            </div>
            <div
              class="pt-2 body-1"
              v-if="mime_type == DEFINES.MIME_TYPE.VIDEO"
            >
              <span class="greyDarken1--text font-weight-medium"
                >Note :&nbsp;</span
              >
              <i
                class="lightteal--text"
                v-html="$t('upload.video.allowed_format')"
              ></i>
            </div>
            <div
              class="pt-2 body-1"
              v-if="mime_type == DEFINES.MIME_TYPE.VTT"
            >
              <span class="greyDarken1--text font-weight-medium"
                >Note :&nbsp;</span
              >
              <i
                class="lightteal--text"
                v-html="$t('upload.subtitle.allowed_format')"
              ></i>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions class="px-3">
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            :disabled="is_uploading"
            class="mx-1"
            outline
            @click="resetAndClose()"
            >{{ $t('rms.button.done') }}</v-btn
          >
          <ConfirmationDialog
            v-if="is_uploading"
            class="pr-2"
            :desc="'assets_manager.upload.confirmation.description'"
            :action="getDialogAction(null, 'rms_close', 'error', 'cancel')"
            :headline="'assets_manager.upload.confirmation.heading'"
            :button_type="'button'"
          ></ConfirmationDialog>
          <v-btn v-else color="grey" @click="resetAndClose()" outline>
            {{ $t('rms.button.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import {buildHeaders} from '../../api/_manager';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import * as API from '../../api/_manager';
// import from local copy
import vue2Dropzone from '@/components/local/vue2-dropzone/src/index.js';
import '@/components/local/vue2-dropzone/dist/vue2Dropzone.min.css';
import i18n from '@/i18n';
import GDriveSelector from "@/components/media/GDriveUploader/Selector";
import { mapGetters } from 'vuex';

export default {
  name: 'MediaUploader',
  props: [
    'show', 'model', 'model_type', 'mime_type', "collection",
    "is_subtitle_upload",
    "lang_subtitle",
    "upload_type"
  ],
  data() {
    return {
      is_uploading: false,
      dropzone_custom_error: null,
    };
  },
  components: {
    vueDropzone: vue2Dropzone,
    ConfirmationDialog,
    GDriveSelector
  },
  mounted() {},
  computed: {
    dropzoneOptions() {
      return {
        url: `${this.DEFINES.BASE_API_URL}/member/company/${this.$route.params.company_id}/${this.model_type}/${this.model.id}/upload`,
        thumbnailWidth: 200,
        maxFilesize: this.collection == 'public' ? 1 : 10240, // 10 GB
        chunkSize: 50000000, // 50 MB
        chunking: true,
        destroyDropzone: false,
        headers: buildHeaders().headers,
        retryChunks: true,
        retryChunksLimit: 2880, // 1 day average retry is 60 seconds
        timeout: 900000, //900 sec = 15 mins
        acceptedFiles: this.acceptedFiles,
      };
    },
    ...mapGetters('auth', {
      isUploadModeLogin : 'isUploadModeLogin',
    }),
    acceptedFiles(){
      if(this.isUploadModeLogin){
        return "video/mp4, video/quicktime";
      }
      return this.supportedMimeTypes(this.mime_type)
    }
  },
  methods: {
    supportedMimeTypes(mime_type) {
      switch (mime_type) {
        case 'image':
          return "image/*";
        case 'video':
          return ".mp4,.mov";
        case 'vtt':
          return ".srt,.vtt";
        default:
          return null;
      }
    },
    async successUpload(file, response) {
      try {
        var response = JSON.parse(file.xhr.responseText);
        if (response.error) {
          this.$refs.myVueDropzone.removeFile(file);
          await this.$store.dispatch('app/showErrorMessageDialog', {
            error: response.error,
          });
        } else {
          var message = this.isUploadModeLogin ? this.$t('upload_link.upload.sucess') : i18n.t('rms.upload.success');
          this.$emit('uploadedSuccessfully',response.data);
          await this.$store.dispatch('app/showMessage', {
            message: message,
            color: 'success',
          });
        }
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      }
    },

    async queueCompelete() {
      try {
        if(this.isUploadModeLogin){
          this.is_uploading = false;
          return;
        }
        await this.$store.dispatch('files/get', {
          model_type: this.model_type,
          model_id: this.model.id,
        });
        this.is_uploading = false;
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      }
    },
    resetAndClose() {
      if(this.upload_type != 'gdriveUpload'){
        this.$refs.myVueDropzone.removeAllFiles(true);
      }
      this.$emit('close');
    },
    getDialogAction(update_status, btn_icon, btn_color, btn_label = null) {
      return {
        button_icon: btn_icon,
        button_color: btn_color,
        button_label: btn_label,
        method: this.resetAndClose,
        payload: update_status,
      };
    },
    appendData(file, xhr, formData) {
      formData.append('collection', this.collection);
      if(this.is_subtitle_upload){
        formData.append('is_subtitle', true);
        formData.append('lang_subtitle', this.lang_subtitle);
      }
        // This will track all request so we can get the correct request that returns final response:
        // We will change the load callback but we need to ensure that we will call original
        // load callback from dropzone
        var dropzoneOnLoad = xhr.onload;
        xhr.onload = function (e) {
            dropzoneOnLoad(e)

            // Check for final chunk and get the response
            var uploadResponse = JSON.parse(xhr.responseText)
            if (typeof uploadResponse.name === 'string') {
                $list.append('<li>Uploaded: ' + uploadResponse.path + uploadResponse.name + '</li>')
            }
        }
    },
    handleFilesAdded(files) {
      let self = this
      files.forEach((file) => {
        let uploaded_file_type = file.type.split('/')[0]
        if (uploaded_file_type === self.mime_type) {
          self.is_uploading = true
          self.dropzone_custom_error = null
        } else {
          self.$refs.myVueDropzone.removeFile(file);
          self.dropzone_custom_error = `${ this.$t('upload.img.mime_type_error') } ${self.supportedMimeTypes(self.mime_type)}`
        }
      })
    },
    reportError(file, message, xhr) {
      throw message;
    },

    _isImgUploading(){
      return this.collection == 'public' && this.is_subtitle_upload == undefined;
    }
  },
};
</script>
